import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { TierKey } from "@/config/tiers";
import { useInstance } from "@/contexts/InstanceContext";
import { seatAllotmentFromServer, type SeatAllotment } from "@/utils/seatAllotment";

/**
 * `resolved`   — the paid state below is authoritative for the selected instance.
 * `unresolved` — billing could not be associated with this instance. The
 *                persisted tier/seats/trial are still accurate, but the app must
 *                NOT claim there is no paid subscription.
 */
export type BillingResolution = "resolved" | "unresolved";

interface SubscriptionState {
  subscribed: boolean;
  tier: TierKey;
  /** Finite allotment, or null when unknown/unlimited — never 0 as a sentinel. */
  seats: number | null;
  /** True only for contractually unlimited tiers (never Infinity). */
  seatsUnlimited: boolean;
  /** Typed three-state seat allotment for the UI. */
  seatAllotment: SeatAllotment;
  /** An active subscription exists whose price maps to no known tier. */
  priceUnrecognized: boolean;
  subscriptionEnd: string | null;
  trial: boolean;
  trialEndsAt: string | null;
  priceId: string | null;
  subscriptionId: string | null;
  billingResolution: BillingResolution;
  loading: boolean;
  refresh: () => Promise<void>;
  /** Current workspace this entitlement belongs to (checkout binds to it). */
  instanceId: string | null;
}

const SubscriptionContext = createContext<SubscriptionState>({
  subscribed: false,
  tier: "free",
  seats: null,
  seatsUnlimited: false,
  seatAllotment: { kind: "unknown" },
  priceUnrecognized: false,
  subscriptionEnd: null,
  trial: false,
  trialEndsAt: null,
  priceId: null,
  subscriptionId: null,
  billingResolution: "unresolved",
  loading: true,
  refresh: async () => {},
  instanceId: null,
});

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { currentInstanceId } = useInstance();
  const [subscribed, setSubscribed] = useState(false);
  const [tier, setTier] = useState<TierKey>("free");
  const [seats, setSeats] = useState<number | null>(null);
  const [seatsUnlimited, setSeatsUnlimited] = useState(false);
  const [priceUnrecognized, setPriceUnrecognized] = useState(false);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [trial, setTrial] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [priceId, setPriceId] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [billingResolution, setBillingResolution] = useState<BillingResolution>("unresolved");
  const [loading, setLoading] = useState(true);

  // Monotonic generation: every instance change (and every newer refresh)
  // invalidates any response still in flight, so a slow reply for a previous
  // instance can never repaint the current instance's entitlement.
  const generationRef = useRef(0);

  const resetState = useCallback(() => {
    setSubscribed(false);
    setTier("free");
    setSeats(null);
    setSeatsUnlimited(false);
    setPriceUnrecognized(false);
    setSubscriptionEnd(null);
    setTrial(false);
    setTrialEndsAt(null);
    setPriceId(null);
    setSubscriptionId(null);
    setBillingResolution("unresolved");
  }, []);

  // Never let a previous instance's entitlement linger while switching.
  useEffect(() => {
    generationRef.current += 1;
    resetState();
    setLoading(true);
  }, [currentInstanceId, resetState]);

  const refresh = useCallback(async () => {
    const generation = ++generationRef.current;
    const isCurrent = () => generation === generationRef.current;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      // Every early path is guarded by the same generation ownership.
      if (!isCurrent()) return;
      if (!session) {
        resetState();
        setLoading(false);
        return;
      }

      if (!currentInstanceId) {
        resetState();
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke("check-subscription", {
        body: { instance_id: currentInstanceId },
      });

      // The selected instance (or a newer refresh) may have superseded this
      // request while it was in flight — discard the stale response entirely.
      if (!isCurrent()) return;

      if (error) {
        console.error("check-subscription error:", error);
        // Fail closed: never present another instance's entitlement.
        resetState();
        setLoading(false);
        return;
      }

      setSubscribed(data.subscribed ?? false);
      setTier((data.tier as TierKey) ?? "free");
      setSeats(typeof data.seats === "number" && Number.isFinite(data.seats) && data.seats > 0 ? data.seats : null);
      setSeatsUnlimited(data.seats_unlimited === true);
      setPriceUnrecognized(data.price_unrecognized === true);
      setSubscriptionEnd(data.subscription_end ?? null);
      setTrial(data.trial ?? false);
      setTrialEndsAt(data.trial_ends_at ?? null);
      setPriceId(data.price_id ?? null);
      setSubscriptionId(data.subscription_id ?? null);
      setBillingResolution((data.billing_resolution as BillingResolution) ?? "unresolved");
    } catch (err) {
      if (!isCurrent()) return;
      console.error("Subscription check failed:", err);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [currentInstanceId, resetState]);


  useEffect(() => {
    refresh();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "TOKEN_REFRESHED") {
        refresh();
      }
    });

    // Visibility-aware periodic refresh: pause when tab is hidden
    let interval: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (!interval) {
        interval = setInterval(refresh, 60_000);
      }
    };

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh(); // Refresh immediately when tab becomes visible
        startPolling();
      } else {
        stopPolling();
      }
    };

    // Only poll if tab is visible
    if (document.visibilityState === 'visible') {
      startPolling();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      subscription.unsubscribe();
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refresh]);

  const seatAllotment = seatAllotmentFromServer({ seats, seatsUnlimited });

  return (
    <SubscriptionContext.Provider value={{ subscribed, tier, seats, seatsUnlimited, seatAllotment, priceUnrecognized, subscriptionEnd, trial, trialEndsAt, priceId, subscriptionId, billingResolution, loading, refresh, instanceId: currentInstanceId ?? null }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}
